"use client";

import { useState } from "react";
import { BiFilter } from "react-icons/bi";
import { motion } from "framer-motion";

import { FACET_KEYS, Facets } from "@/@types/filters";
import Badge from "@/components/Badge";
import useProductFilters from "@/hooks/useProductFilters";
import ActiveFilterChips from "./ActiveFilterChips";
import FilterGroup from "./FilterGroup";
import FiltersDrawer from "./FiltersDrawer";
import styles from "./Filters.module.scss";
import { montserrat } from "./font";

interface Props {
  facets: Facets;
  total: number;
  /** `total` é um piso (a busca tem teto de candidatos): exibe "250+". */
  totalCapped?: boolean;
}

const TITLE_ID = "filtros-titulo";

export default function Filters({ facets, total, totalCapped = false }: Props) {
  const [open, setOpen] = useState(false);
  const { activeCount, filters, isPending, isSelected, toggle, remove, clear } =
    useProductFilters();

  const hasOptions = FACET_KEYS.some((key) => facets[key]?.length);
  const count = `${total}${totalCapped ? "+" : ""} ${total === 1 && !totalCapped ? "produto" : "produtos"}`;

  return (
    <section className={styles.filters}>
      <div className={styles.filters__container}>
        <motion.button
          type="button"
          className={styles.button}
          style={montserrat.style}
          onClick={() => setOpen((value) => !value)}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          aria-expanded={open}
          aria-haspopup="dialog"
          disabled={!hasOptions}
        >
          <BiFilter aria-hidden="true" />
          Filtrar
          {/* O badge conta filtros ativos. Antes mostrava o total de produtos. */}
          <Badge value={activeCount} color="#072B1C" position="middle" />
        </motion.button>

        <ActiveFilterChips filters={filters} onRemove={remove} onClear={clear} />

        <span className={styles.resultCount} aria-live="polite">
          {isPending ? "Atualizando…" : count}
        </span>
      </div>

      <FiltersDrawer open={open} onClose={() => setOpen(false)} titleId={TITLE_ID}>
        <div className={styles.panelBody} data-pending={isPending || undefined}>
          {FACET_KEYS.map((key) => (
            <FilterGroup
              key={key}
              facetKey={key}
              options={facets[key] ?? []}
              isSelected={isSelected}
              onToggle={toggle}
            />
          ))}
        </div>

        <footer className={styles.panelFooter}>
          {activeCount > 0 && (
            <button type="button" className={styles.clear} onClick={clear}>
              Limpar
            </button>
          )}

          <button type="button" className={styles.apply} onClick={() => setOpen(false)}>
            Ver {count}
          </button>
        </footer>
      </FiltersDrawer>
    </section>
  );
}
